import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from './VisibilityPage.module.css';
import { PageShell, type PageShellRender } from '@/components/PageShell';
import { KpiStrip, type Kpi } from '@/components/KpiStrip';
import { Panel, SegmentedControl, EmptyState } from '@/components/Panel';
import { MetricTable, Tag, type MetricColumn } from '@/components/MetricTable';
import { LineChart } from '@/components/charts/LineChart';
import { DataSourceBanner } from '@/features/availability/DataSourceBanner';
import {
  fetchVisibility, type Cell, type RivalRow, type SovProductRow, type SovRow,
  type VisibilityOverview,
} from '@/lib/pages.api';
import { deltaPct, pctOf, plain, rank, shortDate, tone } from '@/lib/money';
import { titleCase } from '@/lib/format';

/**
 * Visibility — share of voice and search rank.
 *
 * WHICH SOV THIS IS, SAID EVERY TIME. Two different SOVs exist on this
 * platform: the Hector-derived estimate shown here — daily, city-capable, split
 * into ad and organic — and Blinkit's own reported figure, which is blended,
 * national and trailing-30-day. They have different provenance, window and
 * grain, so they are expected to disagree and must never be reconciled. The
 * banner below is not a caveat, it is the label on the measurement.
 *
 * Every share on the page is pooled first and divided once, against the weight
 * of the slots each crawl actually returned — including the crawls the brand
 * did not appear in at all. Averaging daily shares, or dropping the days with
 * no appearance, both read high and both read high silently.
 */

const sub = (c: Cell, invert = false): { text: string; tone?: 'up' | 'down' } | undefined => {
  const d = deltaPct(c.delta_pct, 1);
  if (!d) return undefined;
  const t = tone(c.delta_pct, invert);
  return t ? { text: d, tone: t } : { text: d };
};

/** A share moved by a fraction of a point reads better as points than percent. */
const pointsMoved = (c: Cell): { text: string; tone?: 'up' | 'down' } | undefined => {
  if (c.delta == null) return undefined;
  const t = tone(c.delta);
  const text = `${c.delta >= 0 ? '+' : '−'}${Math.abs(c.delta).toFixed(2)} pts`;
  return t ? { text, tone: t } : { text };
};

export function VisibilityPage({
  onNavigate,
  onSignOut,
}: {
  onNavigate?: (section: string, page: string) => void;
  onSignOut?: () => void;
}) {
  const [resolved, setResolved] = useState<VisibilityOverview['window'] | undefined>(undefined);
  return (
    <PageShell
      title="Visibility"
      activeNavId="visibility"
      activeSection="visibility"
      chipKinds={['category', 'city']}
      {...(onNavigate ? { onNavigate } : {})}
      {...(onSignOut ? { onSignOut } : {})}
      resolved={resolved}
    >
      {(ctx) => <VisibilityBody ctx={ctx} onResolved={setResolved} />}
    </PageShell>
  );
}

function VisibilityBody({
  ctx,
  onResolved,
}: {
  ctx: PageShellRender;
  onResolved: (w: VisibilityOverview['window']) => void;
}) {
  const [slot, setSlot] = useState<'all' | 'morning' | 'evening'>('all');
  const [chartMetric, setChartMetric] = useState<'sov' | 'rank'>('sov');
  const [keyword, setKeyword] = useState<string | undefined>(undefined);

  const params = useMemo(
    () => ({
      customer_name: ctx.customer,
      window: ctx.range,
      filters: {
        ...(ctx.scope.category ? { category: ctx.scope.category } : {}),
        ...(ctx.scope.city ? { city: ctx.scope.city } : {}),
        ...(keyword ? { keyword } : {}),
        ...(slot !== 'all' ? { slot } : {}),
      },
      limit: 12,
    }),
    [ctx.customer, ctx.range, ctx.scope.category, ctx.scope.city, keyword, slot],
  );

  const query = useQuery({
    queryKey: ['visibility-overview', params],
    queryFn: ({ signal }) => fetchVisibility(params, signal),
    enabled: ctx.customer !== '',
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  const data = query.data;
  const win = data?.window;
  useEffect(() => {
    if (win) onResolved(win);
  }, [win, onResolved]);
  const loading = query.isPending && !data;

  const kpis: Kpi[] = useMemo(() => {
    const k = data?.kpis;
    const c = data?.response_meta.coverage;
    return [
      {
        key: 'overall',
        label: 'Overall SOV',
        value: pctOf(k?.overall_sov.value ?? null, 2),
        delta: k?.overall_sov.delta != null ? `${k.overall_sov.delta >= 0 ? '+' : '−'}${Math.abs(k.overall_sov.delta).toFixed(2)} pts` : undefined,
        tone: tone(k?.overall_sov.delta),
        help: data?.response_meta.sov_note,
        emphasis: true,
      },
      {
        key: 'ad',
        label: 'Ad SOV',
        value: pctOf(k?.ad_sov.value ?? null, 2),
        delta: k?.ad_sov.delta != null ? `${k.ad_sov.delta >= 0 ? '+' : '−'}${Math.abs(k.ad_sov.delta).toFixed(2)} pts` : undefined,
        tone: tone(k?.ad_sov.delta),
      },
      {
        key: 'organic',
        label: 'Organic SOV',
        value: pctOf(k?.organic_sov.value ?? null, 2),
        delta: k?.organic_sov.delta != null ? `${k.organic_sov.delta >= 0 ? '+' : '−'}${Math.abs(k.organic_sov.delta).toFixed(2)} pts` : undefined,
        tone: tone(k?.organic_sov.delta),
      },
      {
        key: 'ad_rank',
        label: 'Avg ad rank',
        value: rank(k?.avg_ad_rank.value ?? null),
        delta: deltaPct(k?.avg_ad_rank.delta_pct),
        // A rising rank number is a worse position.
        tone: tone(k?.avg_ad_rank.delta_pct, true),
        help: 'Averaged only over the crawls the brand actually appeared in. A missing appearance is never counted as rank 0 or rank 76.',
      },
      {
        key: 'organic_rank',
        label: 'Avg organic rank',
        value: rank(k?.avg_organic_rank.value ?? null),
        delta: deltaPct(k?.avg_organic_rank.delta_pct),
        tone: tone(k?.avg_organic_rank.delta_pct, true),
      },
      {
        key: 'keywords',
        label: 'Keywords appeared on',
        value: plain(k?.keywords_tracked.value ?? null),
        foot: c ? `of ${plain(c.keywords_in_scope)} tracked` : undefined,
      },
      {
        key: 'cities',
        label: 'Cities crawled',
        value: plain(k?.cities_tracked.value ?? null),
      },
      {
        key: 'screen',
        label: 'Screen ad share',
        value: pctOf(k?.screen_ad_share.value ?? null),
        help: 'Of the slots these searches actually returned, the share that were sponsored — how ad-heavy the screen is before anyone bids.',
      },
    ];
  }, [data]);

  const chart = useMemo(() => {
    const s = data?.series ?? [];
    const labels = s.map((p) => shortDate(p.day));
    if (chartMetric === 'rank') {
      return {
        labels,
        yMax: undefined,
        series: [
          {
            key: 'ad_rank',
            label: 'Avg ad rank',
            color: '#d03941',
            points: s.map((p) => p.avg_ad_rank),
            format: (v: number) => rank(v),
          },
          {
            key: 'organic_rank',
            label: 'Avg organic rank',
            color: '#342885',
            points: s.map((p) => p.avg_organic_rank),
            format: (v: number) => rank(v),
          },
        ],
      };
    }
    return {
      labels,
      yMax: undefined,
      series: [
        {
          key: 'overall',
          label: 'Overall SOV',
          color: '#342885',
          points: s.map((p) => p.overall_sov),
          format: (v: number) => pctOf(v, 2),
        },
        {
          key: 'ad',
          label: 'Ad SOV',
          color: '#d03941',
          points: s.map((p) => p.ad_sov),
          format: (v: number) => pctOf(v, 2),
        },
        {
          key: 'organic',
          label: 'Organic SOV',
          color: '#008632',
          points: s.map((p) => p.organic_sov),
          format: (v: number) => pctOf(v, 2),
        },
      ],
    };
  }, [data, chartMetric]);

  const keywordColumns: MetricColumn<SovRow>[] = [
    { key: 'kw', header: 'Keyword', align: 'left', width: '24%', render: (r) => r.label },
    {
      key: 'overall',
      header: 'Overall SOV',
      render: (r) => pctOf(r.overall_sov.value, 2),
      sub: (r) => pointsMoved(r.overall_sov),
    },
    { key: 'ad', header: 'Ad SOV', render: (r) => pctOf(r.ad_sov.value, 2), sub: (r) => pointsMoved(r.ad_sov) },
    {
      key: 'organic',
      header: 'Organic SOV',
      render: (r) => pctOf(r.organic_sov.value, 2),
      sub: (r) => pointsMoved(r.organic_sov),
    },
    { key: 'adrank', header: 'Ad rank', render: (r) => rank(r.avg_ad_rank.value), sub: (r) => sub(r.avg_ad_rank, true) },
    {
      key: 'orgrank',
      header: 'Organic rank',
      render: (r) => rank(r.avg_organic_rank.value),
      sub: (r) => sub(r.avg_organic_rank, true),
    },
    {
      key: 'volume',
      header: 'Monthly searches',
      help: 'National, last 30 days, refreshed weekly. It has no city cut and is never summed across keywords.',
      render: (r) => plain(r.search_volume ?? null),
    },
    { key: 'screen', header: 'Ad-heavy', render: (r) => pctOf(r.screen_ad_share ?? null) },
  ];

  const cityColumns: MetricColumn<SovRow>[] = [
    {
      key: 'city',
      header: 'City',
      align: 'left',
      width: '24%',
      render: (r) => titleCase(r.label) ?? r.label,
    },
    {
      key: 'overall',
      header: 'Overall SOV',
      render: (r) => pctOf(r.overall_sov.value, 2),
      sub: (r) => pointsMoved(r.overall_sov),
    },
    { key: 'ad', header: 'Ad SOV', render: (r) => pctOf(r.ad_sov.value, 2), sub: (r) => pointsMoved(r.ad_sov) },
    {
      key: 'organic',
      header: 'Organic SOV',
      render: (r) => pctOf(r.organic_sov.value, 2),
      sub: (r) => pointsMoved(r.organic_sov),
    },
    { key: 'adrank', header: 'Ad rank', render: (r) => rank(r.avg_ad_rank.value) },
    { key: 'orgrank', header: 'Organic rank', render: (r) => rank(r.avg_organic_rank.value) },
    { key: 'app', header: 'Appearances', render: (r) => plain(r.appearances) },
  ];

  const rivalColumns: MetricColumn<RivalRow>[] = [
    {
      key: 'brand',
      header: 'Brand',
      align: 'left',
      width: '28%',
      render: (r) => (
        <span className={styles.brandCell}>
          {r.brand}
          {r.is_own ? <Tag tone="good">yours</Tag> : null}
        </span>
      ),
    },
    {
      key: 'overall',
      header: 'Overall SOV',
      render: (r) => pctOf(r.overall_sov.value, 2),
      sub: (r) => pointsMoved(r.overall_sov),
    },
    { key: 'ad', header: 'Ad SOV', render: (r) => pctOf(r.ad_sov.value, 2), sub: (r) => pointsMoved(r.ad_sov) },
    {
      key: 'organic',
      header: 'Organic SOV',
      render: (r) => pctOf(r.organic_sov.value, 2),
      sub: (r) => pointsMoved(r.organic_sov),
    },
    { key: 'app', header: 'Appearances', render: (r) => plain(r.appearances) },
  ];

  const productColumns: MetricColumn<SovProductRow>[] = [
    { key: 'sku', header: 'Product', align: 'left', width: '32%', render: (p) => p.sku_name ?? p.product_id },
    { key: 'kw', header: 'Keywords', render: (p) => plain(p.keywords) },
    { key: 'overall', header: 'Overall SOV', render: (p) => pctOf(p.overall_sov, 2) },
    { key: 'ad', header: 'Ad SOV', render: (p) => pctOf(p.ad_sov, 2) },
    { key: 'organic', header: 'Organic SOV', render: (p) => pctOf(p.organic_sov, 2) },
    { key: 'adrank', header: 'Ad rank', render: (p) => rank(p.avg_ad_rank) },
    { key: 'orgrank', header: 'Organic rank', render: (p) => rank(p.avg_organic_rank) },
  ];

  const coverage = data?.response_meta.coverage;

  return (
    <>
      {query.isError && (
        <DataSourceBanner kind="error" detail={query.error.message} />
      )}

      <div className={styles.provenance}>
        <strong>Hector-derived SOV</strong>
        <span>
          Attention-weighted share of the search slots actually crawled, pooled across the
          window and divided once. This is <em>not</em> Blinkit’s reported SOV — that one is
          blended, national and trailing-30-day, and the two are not reconcilable.
        </span>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <Panel
        title="Daily trend"
        caption={
          coverage
            ? `${plain(coverage.crawls)} crawls across ${plain(coverage.keywords_in_scope)} keywords and ${plain(coverage.cities_in_scope)} cities · ${coverage.days_with_crawls} of ${coverage.days_in_window} days carried a crawl`
            : undefined
        }
        actions={
          <div className={styles.controls}>
            <SegmentedControl
              label="Time slot"
              value={slot}
              onChange={setSlot}
              options={[
                { value: 'all', label: 'Both slots' },
                { value: 'morning', label: 'Morning' },
                { value: 'evening', label: 'Evening' },
              ]}
            />
            <SegmentedControl
              label="Trend metric"
              value={chartMetric}
              onChange={setChartMetric}
              options={[
                { value: 'sov', label: 'Share' },
                { value: 'rank', label: 'Rank' },
              ]}
            />
          </div>
        }
        footnote={
          chartMetric === 'rank'
            ? 'Lower is better. Ranks are averaged only over the crawls the brand appeared in — a day with no appearance leaves a gap, not a zero.'
            : undefined
        }
      >
        {chart.labels.length === 0 ? (
          <EmptyState>No crawls landed in this window for the keywords in scope.</EmptyState>
        ) : (
          <LineChart labels={chart.labels} series={chart.series} height={250} />
        )}
      </Panel>

      {(data?.slots.length ?? 0) > 0 && (
        <Panel
          title="Morning against evening"
          caption="Both slots are crawled on a fixed schedule, so the difference is the screen changing, not sampling."
          dense
        >
          <MetricTable
          searchable
            caption="SOV by time slot"
            columns={[
              { key: 'slot', header: 'Slot', align: 'left', width: '30%', render: (r: SovRow) => titleCase(r.label) ?? r.label },
              { key: 'overall', header: 'Overall SOV', render: (r: SovRow) => pctOf(r.overall_sov.value, 2) },
              { key: 'ad', header: 'Ad SOV', render: (r: SovRow) => pctOf(r.ad_sov.value, 2) },
              { key: 'organic', header: 'Organic SOV', render: (r: SovRow) => pctOf(r.organic_sov.value, 2) },
            ]}
            rows={data?.slots ?? []}
            rowKey={(r) => r.id}
            loading={loading}
          />
        </Panel>
      )}

      <Panel
        title="Keywords"
        caption="Ranked by your own share. A keyword you did not appear on at all stays in the list with a blank share — that is the most actionable row here."
        actions={
          keyword ? (
            <button type="button" className={styles.clear} onClick={() => { setKeyword(undefined); }}>
              Clear “{keyword}” ✕
            </button>
          ) : null
        }
        dense
      >
        <MetricTable
          searchable
          caption="SOV by keyword"
          columns={keywordColumns}
          rows={data?.keywords ?? []}
          rowKey={(r) => r.id}
          loading={loading}
          onRowClick={(r) => { setKeyword(keyword === r.label ? undefined : r.label); }}
          isSelected={(r) => r.label === keyword}
          empty="No crawled keywords for this brand in this window."
        />
      </Panel>

      <Panel
        title="Cities"
        caption="Each city measured against the slots crawled in that city, for the same keyword set."
        dense
      >
        <MetricTable
          searchable
          caption="SOV by city"
          columns={cityColumns}
          rows={data?.cities ?? []}
          rowKey={(r) => r.id}
          loading={loading}
          empty="No cities crawled for these keywords."
        />
      </Panel>

      <Panel
        title="Who is on your keywords"
        caption="Ordered by share, and the movement column is the one to read — who gained what you lost."
        footnote={data?.response_meta.rivals_note}
        dense
      >
        <MetricTable
          searchable
          caption="Competitor share of voice"
          columns={rivalColumns}
          rows={data?.rivals ?? []}
          rowKey={(r) => r.brand}
          loading={loading}
          isSelected={(r) => r.is_own}
          empty="No competing brands appeared on these keywords."
        />
      </Panel>

      {(data?.products.length ?? 0) > 0 && (
        <Panel
          title="Your products on the screen"
          caption="Pooled across cities: a product's share is measured against every crawl its keywords appeared in."
          dense
        >
          <MetricTable
          searchable
            caption="SOV by product"
            columns={productColumns}
            rows={data?.products ?? []}
            rowKey={(p) => p.product_id}
            loading={loading}
          />
        </Panel>
      )}
    </>
  );
}
